// wiring.js — pure, unit-testable mapping between the live craftjs Text node props and
// the WL-011 TextEditor's controlled `value`. WL-011b / WL-011d.
//
// The live Text node stores type styles under `headingProps`/`paragraphProps`
// ({ fontColor, fontFamily, fontSize }), line spacing under `lineHeight`, and padding
// under `padding` + `isApplyPaddingOnAllSides`. This normalizes those to the editor's
// { heading:{color,font,size}, paragraph:{…}, lineSpacing, padding, applyPaddingAll }.
//
// WL-011d — the rich body is now authored with the REAL Froala editor mounted in the
// v2 pane (Text/TextRichField), so `body` <-> `text` is the raw Froala HTML, byte-
// identical to the legacy Text block. The `text` field is still round-tripped verbatim
// (no re-parse). The style path (heading/paragraph/lineSpacing/padding) is applied
// WITHOUT touching `text` (applyValueToProps(..., { includeBody: false })) so a style
// tweak never clobbers an in-flight rich-text edit; the Froala field writes `text`
// directly on its own debounce, mirroring the legacy TextForm onChange.

// craftjs node props  ->  TextEditor `value`
export function nodePropsToValue(props = {}) {
  const p = props || {};
  const hp = p.headingProps || {};
  const pp = p.paragraphProps || {};
  return {
    body: p.text || "",
    heading: {
      color: hp.fontColor ?? "#123051",
      font: hp.fontFamily ?? "DM Sans",
      size: hp.fontSize ?? 20,
    },
    paragraph: {
      color: pp.fontColor ?? "#123051",
      font: pp.fontFamily ?? "DM Sans",
      size: pp.fontSize ?? 16,
    },
    lineSpacing: p.lineHeight ?? 1.15,
    padding: { top: 0, right: 0, bottom: 0, left: 0, ...(p.padding || {}) },
    applyPaddingAll: !!p.isApplyPaddingOnAllSides,
  };
}

// TextEditor `value`  ->  mutate a craftjs draft props object in place.
// options.includeBody (default true): when false, `text` is left untouched so a
// style-only edit never overwrites an in-flight rich-text body edit (WL-011d).
export function applyValueToProps(next = {}, props = {}, options = {}) {
  const { includeBody = true } = options;
  if (!props.headingProps) props.headingProps = {};
  if (!props.paragraphProps) props.paragraphProps = {};
  if (!props.padding) props.padding = {};

  if (includeBody) props.text = next.body;
  props.headingProps = {
    ...props.headingProps,
    fontColor: next.heading?.color,
    fontFamily: next.heading?.font,
    fontSize: next.heading?.size,
  };
  props.paragraphProps = {
    ...props.paragraphProps,
    fontColor: next.paragraph?.color,
    fontFamily: next.paragraph?.font,
    fontSize: next.paragraph?.size,
  };
  props.lineHeight = next.lineSpacing;
  props.padding = { ...props.padding, ...(next.padding || {}) };
  props.isApplyPaddingOnAllSides = !!next.applyPaddingAll;
  return props;
}

const wiring = { nodePropsToValue, applyValueToProps };
export default wiring;
